// main.ts — boot klienta CS 1.6 w przeglądarce (cs.suby.pl).
// Buduje window.CS — ZAMROŻONY kontrakt boot dla index.html / ui.js:
//   CS.start({ nick, wsPath, onProgress, onState }) -> Promise<void>
//   CS.engine — instancja Xash3DWS po starcie
//   CS.exec(cmd) — komenda konsoli silnika
// Content ładowany z /content/manifest.json (paczki zip, cache przez Cache API
// wersjonowany rewizją manifestu — stare cache są usuwane).
import { unzipSync } from 'fflate';
import { Xash3DWS } from './wsnet';

type StartOpts = {
  nick?: string;
  wsPath?: string; // np. /game
  map?: string; // wymuszenie paczki mapy (domyślnie: mapa z /api/status)
  // tryb odtwarzania: pobierz demo IDEM i odtwórz lokalnie (bez sieci);
  // atSeconds > 0 = dołączenie w trakcie (fast-forward przez sys_timescale)
  replay?: { file: string; map: string; atSeconds?: number };
  onProgress?: (label: string, loaded: number, total: number) => void;
  onState?: (state: string) => void;
};

type Manifest = {
  rev: string;
  packs: {
    common: { file: string; size: number };
    maps: Record<string, { file: string; size: number }>;
  };
};

const ENGINE_FILES = {
  xash: '/engine/xash.wasm',
  filesystem: '/engine/filesystem_stdio.wasm',
  gl4es: '/engine/libref_webgl2.wasm',
  menu: '/engine/menu_emscripten_wasm32.wasm',
  client: '/engine/client_emscripten_wasm32.wasm',
  server: '/engine/cs_emscripten_wasm32.wasm',
  extras: '/engine/extras.pk3',
};

// Jeden trwały bucket; wpisy kluczowane URL-em (nazwy paczek mają content-hash,
// więc URL jednoznacznie identyfikuje treść). Dzięki temu ingest nowej mapy
// (bump rev manifestu) NIE unieważnia pobranego common (~205 MB).
const CACHE_NAME = 'cs-content-v1';

async function cachedFetch(
  url: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<ArrayBuffer> {
  let cache: Cache | undefined;
  try {
    cache = await caches.open(CACHE_NAME);
    const hit = await cache.match(url);
    if (hit) return await hit.arrayBuffer();
  } catch { /* Cache API niedostępne (np. private mode) — lecimy bez cache */ }

  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
  const total = Number(res.headers.get('Content-Length')) || 0;
  const reader = res.body!.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onProgress?.(received, total);
  }
  const blob = new Blob(chunks as BlobPart[]);
  const buf = await blob.arrayBuffer();
  try { await cache?.put(url, new Response(blob)); } catch { /* quota */ }
  return buf;
}

// Sprzątanie: wpisy spoza aktualnego manifestu + stare buckety per-rev.
async function pruneCache(manifest: Manifest) {
  try {
    const keep = new Set<string>([
      `/content/${manifest.packs.common.file}`,
      ...Object.values(manifest.packs.maps).map((m) => `/content/${m.file}`),
      ...Object.values(ENGINE_FILES),
    ]);
    const cache = await caches.open(CACHE_NAME);
    for (const req of await cache.keys()) {
      if (!keep.has(new URL(req.url).pathname)) await cache.delete(req);
    }
    for (const name of await caches.keys()) {
      if (name.startsWith('cs-content-') && name !== CACHE_NAME) await caches.delete(name);
    }
  } catch { /* noop */ }
}

function writeZipToFS(em: any, zipBuf: ArrayBuffer) {
  const files = unzipSync(new Uint8Array(zipBuf));
  const mkdirs = new Set<string>();
  for (const [name, data] of Object.entries(files)) {
    if (name.endsWith('/')) continue;
    const path = '/rodir/' + name;
    const dir = path.split('/').slice(0, -1).join('/');
    if (!mkdirs.has(dir)) {
      em.FS.mkdirTree(dir);
      mkdirs.add(dir);
    }
    em.FS.writeFile(path, data as Uint8Array);
  }
}

// Mobile: silnik ma natywny touch, ale domyślny touch.cfg z extras.pk3 daje
// ~36 przycisków (zaśmiecony ekran) i magentowy highlight przy naciśnięciu.
// Tu przebudowujemy layout na MINIMALNY (joystick + celowanie + strzał + zoom
// oraz kilka małych akcji w rogach) i wyłączamy fioletowy highlight, który na
// półekranowych strefach _move/_look wyglądał jak wielkie brakujące tekstury
// (raport testera 2026-07-12). Powiększamy też HUD/tablicę wyników na telefon.
// Wszystko przez konsolę silnika (Cmd_ExecuteString) — bez ruszania extras.pk3.
function setupMobileControls(engine: Xash3DWS) {
  const cmd = (s: string) => engine.Cmd_ExecuteString(s);

  const apply = () => {
    // P2: fioletowe prostokąty przy multitouchu = BRAKUJĄCA TEKSTURA wskaźnika
    // ruchu. Domyślny touch.cfg (extras.pk3) ustawia `touch_move_indicator "3"`,
    // ale ten cvar to NAZWA TEKSTURY wskaźnika rysowanego pod palcem w strefie
    // ruchu (engine: "texture for move indicator") — "3" nie jest teksturą, więc
    // silnik rysuje placeholder brakującej tekstury (magenta/czarna szachownica)
    // dokładnie tam, gdzie palec dotyka lewej połowy. Multitouch = widoczne od
    // razu. Pusta wartość = wskaźnik nie jest rysowany. (dowód:
    // .selfcheck/shots-mobile/p2-multitouch.png z przebiegu przed fixem)
    cmd('touch_move_indicator 0');
    // belt-and-suspenders: gdyby wskaźnik kiedyś wrócił (np. re-exec touch.cfg
    // przy changelevel), niech rysuje ISTNIEJĄCY sprite joysticka z extras.pk3
    // zamiast placeholderu brakującej tekstury (domyślny "touch_default/joy" NIE
    // istnieje w naszym extras.pk3 — "touch/gfx/joy.png" istnieje).
    cmd('touch_joy_texture "touch/gfx/joy"');
    // dodatkowo gasimy magentowy highlight naciśnięcia (1.0/0.5/1.0 alpha 1),
    // który na półekranowych strefach też potrafił mignąć fioletem.
    cmd('touch_highlight_a 0');

    // P1: minimalny layout in-game. Zdejmujemy wszystko i budujemy czysty zestaw.
    cmd('touch_removeall');
    // strefy (bez tekstury): lewa połowa = ruch, prawa = celowanie
    cmd('touch_addbutton "move" "" "_move" 0.0 0.0 0.5 1.0 255 255 255 0 0');
    cmd('touch_addbutton "look" "" "_look" 0.5 0.0 1.0 1.0 255 255 255 0 0');
    // GŁÓWNE: duży STRZAŁ (prawy dół) + CELOWNIK/zoom = prawy przycisk myszy
    cmd('touch_addbutton "attack" "touch/gfx/attack" "+attack" 0.84 0.60 0.99 0.96 255 255 255 210 0 1');
    cmd('touch_addbutton "attack2" "touch/gfx/attack2" "+attack2" 0.70 0.64 0.82 0.93 255 255 255 200 0 1');
    // pomocnicze (małe): przeładuj, skok, kucnij (toggle), użyj (defuse/plant)
    cmd('touch_addbutton "reload" "touch/gfx/reload" "+reload" 0.70 0.40 0.80 0.60 255 255 255 170 0 1');
    cmd('touch_addbutton "jump" "touch/gfx/jump" "+jump" 0.90 0.30 1.0 0.55 255 255 255 170 0 1');
    cmd('touch_addbutton "duck" "touch/gfx/duck" "crouchtoggle" 0.575 0.74 0.675 0.96 255 255 255 170 0 1');
    cmd('touch_addbutton "use" "touch/gfx/use" "+use" 0.575 0.51 0.675 0.71 255 255 255 170 0 1');
    // pasek górny (małe): kup, sloty broni, tablica wyników
    cmd('touch_addbutton "buy" "touch/gfx/buy" "buy" 0.14 0.0 0.235 0.135 255 255 255 150 0 1');
    cmd('touch_addbutton "w1" "touch/gfx/w_rifle" "slot1" 0.30 0.0 0.385 0.12 255 255 255 150 0 1');
    cmd('touch_addbutton "w2" "touch/gfx/w_pistol" "slot2" 0.395 0.0 0.48 0.12 255 255 255 150 0 1');
    cmd('touch_addbutton "w3" "touch/gfx/w_knife" "slot3" 0.49 0.0 0.575 0.12 255 255 255 150 0 1');
    cmd('touch_addbutton "score" "touch/gfx/score" "scoreboard" 0.90 0.0 1.0 0.135 255 255 255 150 0 1');
  };

  // P4: powiększ HUD i czcionki na telefonie (celownik/kasa/ammo/timer i przede
  // wszystkim tablica wyników po śmierci były nieczytelne). hud_scale to skala
  // wirtualnego ekranu HUD w silniku; wartość dobrana empirycznie na mobilnym
  // viewporcie (patrz .selfcheck). Console-font też większy.
  cmd('hud_scale 3');
  cmd('con_fontscale 1.6');

  apply();
  // Silnik potrafi ponownie wczytać touch.cfg przy wejściu na mapę/spawnie —
  // po chwili nakładamy layout jeszcze raz, żeby domyślne 36 przycisków nie wróciło.
  setTimeout(apply, 1500);
  setTimeout(apply, 4000);
}

async function start(opts: StartOpts = {}) {
  const {
    nick = 'Gracz', wsPath = '/game', onProgress = () => {}, onState = () => {},
  } = opts;

  onState('status');
  const status = await fetch('/api/status').then((r) => r.json()).catch(() => null);
  const mapName = opts.replay?.map || opts.map || status?.server?.map || 'de_dust2';

  onState('manifest');
  const manifest: Manifest = await fetch('/content/manifest.json').then((r) => r.json());
  await pruneCache(manifest);

  const canvas = document.getElementById('canvas') as HTMLCanvasElement;
  const engine = new Xash3DWS({
    canvas,
    renderer: 'gl4es',
    arguments: ['-windowed', '-game', 'cstrike'],
    libraries: {
      filesystem: ENGINE_FILES.filesystem,
      xash: ENGINE_FILES.xash,
      menu: ENGINE_FILES.menu,
      client: ENGINE_FILES.client,
      server: ENGINE_FILES.server,
      render: { gl4es: ENGINE_FILES.gl4es },
    },
    dynamicLibraries: ['dlls/cs_emscripten_wasm32.wasm', '/rodir/filesystem_stdio.wasm'],
    filesMap: {
      'dlls/cs_emscripten_wasm32.wasm': ENGINE_FILES.server,
      '/rodir/filesystem_stdio.wasm': ENGINE_FILES.filesystem,
    },
  });
  (window as any).CS.engine = engine;

  // równolegle: init silnika, WS, content
  onState('loading');
  const packs: { file: string; size: number }[] = [manifest.packs.common];
  const mapPack = manifest.packs.maps[mapName];
  if (mapPack) packs.push(mapPack);

  const totalBytes = packs.reduce((s, p) => s + (p.size || 0), 0);
  let doneBytes = 0;
  const [zipBufs, extras, demoBuf] = await Promise.all([
    (async () => {
      const bufs: ArrayBuffer[] = [];
      for (const p of packs) { // sekwencyjnie: progres czytelny, mniej RAM na raz
        const buf = await cachedFetch(`/content/${p.file}`, (l) => {
          onProgress('content', doneBytes + l, totalBytes);
        });
        doneBytes += p.size || 0;
        bufs.push(buf);
      }
      return bufs;
    })(),
    cachedFetch(ENGINE_FILES.extras),
    opts.replay
      ? fetch(`/replays/${opts.replay.file}`).then((r) => {
        if (!r.ok) throw new Error(`replay HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      : Promise.resolve(null),
    engine.init(),
    opts.replay ? Promise.resolve() : engine.connectWS(wsUrl(wsPath)),
  ]);

  onState('unpack');
  await new Promise((r) => setTimeout(r, 30)); // oddaj klatkę na render UI
  for (const buf of zipBufs) writeZipToFS(engine.em!, buf);
  engine.em!.FS.writeFile('/rodir/cstrike/extras.pk3', new Uint8Array(extras));
  if (demoBuf) {
    // silnik pisze/czyta demka w ROOCIE gamedira (zmierzone: record rec ->
    // /rodir/cstrike/rec.dem), nie w demos/
    engine.em!.FS.writeFile('/rodir/cstrike/replay.dem', new Uint8Array(demoBuf));
  }
  engine.em!.FS.chdir('/rodir');

  onState('starting');
  engine.main();
  engine.Cmd_ExecuteString('_vgui_menus 0');
  const isTouch = !window.matchMedia('(hover: hover)').matches;
  if (isTouch) {
    engine.Cmd_ExecuteString('touch_enable 1');
    setupMobileControls(engine);
  }
  if (opts.replay) {
    engine.Cmd_ExecuteString('playdemo replay');
    const at = opts.replay.atSeconds || 0;
    if (at > 3) {
      // dołączenie w trakcie: przewiń szybko do zadanego offsetu
      const SPEED = 30;
      engine.Cmd_ExecuteString(`sys_timescale ${SPEED}`);
      setTimeout(() => engine.Cmd_ExecuteString('sys_timescale 1'), (at / SPEED) * 1000);
    }
  } else {
    engine.Cmd_ExecuteString(`name "${nick.replace(/["\\;]/g, '')}"`);
    // netcode: domyślne rate'y silnika są modemowe (rate 25000, ex_interp 0.1
    // = 100 ms sztucznego opóźnienia celów). Transport i tak jest lokalny
    // (WS->UDP na loopbacku), więc podkręcamy do współczesnych wartości;
    // serwer i tak sclampuje do swoich limitów.
    engine.Cmd_ExecuteString('rate 100000');
    engine.Cmd_ExecuteString('cl_cmdrate 100');
    engine.Cmd_ExecuteString('cl_updaterate 100');
    engine.Cmd_ExecuteString('ex_interp 0.01');
    engine.Cmd_ExecuteString('connect 127.0.0.1:8080');
  }

  engine.onNetDown = () => onState('netdown');

  // adaptacja netcode do mocy klienta (rekomendacja port/netcode.md pkt 4):
  // na słabym sprzęcie (SwiftShader/stare telefony) klient ACK-uje zbyt rzadko
  // i serwerowa delta wypada z 64-pakietowego okna ("delta frame is too old").
  // Przy <20 fps schodzimy z updaterate — mniej pakietów = świeższe delty.
  if (!opts.replay) {
    let frames = 0;
    const t0 = performance.now();
    const tick = () => {
      frames += 1;
      if (performance.now() - t0 < 3000) requestAnimationFrame(tick);
      else {
        const fps = frames / 3;
        if (fps < 20) {
          engine.Cmd_ExecuteString('cl_updaterate 30');
          engine.Cmd_ExecuteString('ex_interp 0.05');
        } else if (fps < 45) {
          engine.Cmd_ExecuteString('cl_updaterate 60');
          engine.Cmd_ExecuteString('ex_interp 0.017');
        }
      }
    };
    setTimeout(() => requestAnimationFrame(tick), 5000); // po ustabilizowaniu boot
  }

  // Czyste rozłączenie i zwolnienie runtime'u. Samo `disconnect` usuwa gracza
  // z DS, ale zostawia żywy transport/audio/timery Xash do czasu zebrania
  // całego kontekstu strony, co destabilizuje kolejny WASM w tym samym Chrome.
  let stopped = false;
  window.addEventListener('pagehide', () => {
    if (stopped) return;
    stopped = true;
    try { engine.Cmd_ExecuteString('disconnect'); } catch { /* noop */ }
    try { engine.closeWS(); } catch { /* noop */ }
    try { engine.quit(); } catch { /* noop */ }
  });

  onState('ingame');
  (window as any).CS.onStarted?.();
}

function wsUrl(path: string) {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}${path}`;
}

(window as any).CS = {
  start,
  engine: null as Xash3DWS | null,
  exec: (cmd: string) => (window as any).CS.engine?.Cmd_ExecuteString(cmd),
};
